import { v2Request } from './client';
import { storageGet, storageSet } from '../platform/storage';
import { trackGa4AddToCart } from './firebaseAnalytics';
import { getCachedSession } from './user';
import { validateCartCoupon } from './coupons';

const CART_KEY = 'v2_cart';
const LOCATION_KEY = 'v2_location';

/**
 * Fetch categories from v1-categories.
 * Returns [{ id, name, image, sortOrder, documentId }]
 */
export async function getCatalogCategories() {
  return v2Request('/api/categories');
}

/**
 * Fetch products, optionally filtered by category and branch stock.
 * @param {object} params
 * @param {string}  [params.category]  - category id e.g. "chicken"
 * @param {string}  [params.storeId]   - Firestore store doc id; adds isInStock per variant
 * @param {number}  [params.page=1]
 * @param {number}  [params.limit=20]
 */
export async function getProducts({ category, storeId, page = 1, limit = 20 } = {}) {
  const qs = new URLSearchParams({ status: 'active', page, limit });
  if (category) qs.set('category', category);
  if (storeId) qs.set('storeId', storeId);
  return v2Request(`/api/products?${qs.toString()}`);
}

/**
 * Search active products by name (debounced typeahead on home).
 * @param {object} params
 * @param {string} params.q
 * @param {string} [params.storeId]
 * @param {number} [params.limit=10]
 */
export async function searchProducts({ q, storeId, limit = 10 } = {}) {
  const term = String(q ?? '').trim();
  if (!term) return { data: [] };
  const qs = new URLSearchParams({ status: 'active', q: term, limit: String(limit) });
  if (storeId) qs.set('storeId', storeId);
  return v2Request(`/api/products?${qs.toString()}`);
}

export async function getProductById({ id, storeId } = {}) {
  const qs = new URLSearchParams();
  if (storeId) qs.set('storeId', storeId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return v2Request(`/api/products/${id}${suffix}`);
}

// ---------------------------------------------------------------------------
// Cart — persisted shape:
// { products: [...], totalAmount, totalCount, coupon?: null | { couponCode, couponValue, ... }, instructions?: string }
// Line/qty mutations re-validate an applied coupon when possible; use applyValidatedCouponResponse after validate-coupon.
// ---------------------------------------------------------------------------

const CART_INSTRUCTIONS_MAX = 100;

function normalizeCoupon(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const couponCode = String(raw.couponCode || '').trim().toUpperCase();
  const couponValue = Math.max(0, Number(raw.couponValue) || 0);
  if (!couponCode) return null;
  const out = { couponCode, couponValue };
  if (raw.couponType) out.couponType = String(raw.couponType);
  if (raw.discountType) out.discountType = String(raw.discountType);
  if (raw.freeSkuAction) out.freeSkuAction = String(raw.freeSkuAction);
  if (raw.freeSkuProductId) out.freeSkuProductId = String(raw.freeSkuProductId);
  if (raw.couponTitle) out.couponTitle = String(raw.couponTitle).trim();
  if (raw.freeSkuProductName) out.freeSkuProductName = String(raw.freeSkuProductName).trim();
  return out;
}

export function isFreeSkuAppliedCoupon(coupon) {
  return isFreeSkuCouponMeta(coupon);
}

/** Display copy for checkout when a free-SKU coupon is applied. */
export function getAppliedFreeSkuCouponInfo(cart) {
  const c = cart?.coupon;
  if (!isFreeSkuCouponMeta(c)) return null;
  const products = Array.isArray(cart?.products) ? cart.products : [];
  const freeLine = products.find((p) => p.isFreeSkuItem);
  const productName = c.freeSkuProductName || freeLine?.name || 'Free item';
  const description =
    c.couponTitle && c.couponTitle !== c.couponCode ? c.couponTitle : null;
  return {
    description,
    productName,
    autoAdded: c.freeSkuAction === 'auto_add',
  };
}

function isFreeSkuCouponMeta(coupon) {
  return (
    coupon?.couponType === 'free_sku_on_purchase' ||
    coupon?.discountType === 'free_sku'
  );
}

function stripAutoAddedFreeSkuLines(products) {
  if (!Array.isArray(products)) return [];
  return products.filter((line) => !line?.isFreeSkuItem);
}

async function revalidateCartCouponOnMutation({ mobile, storeId } = {}) {
  const resolvedMobile =
    mobile != null ? String(mobile).trim() : String((await getCachedSession())?.mobile || '').trim();
  if (!resolvedMobile) return clearCartCoupon();

  const cart = await readCart();
  if (!cart.coupon?.couponCode) return cart;

  let resolvedStoreId = storeId != null ? String(storeId).trim() : '';
  if (!resolvedStoreId) {
    const loc = await storageGet(LOCATION_KEY);
    resolvedStoreId = loc?.storeId != null ? String(loc.storeId).trim() : '';
  }

  try {
    const resp = await validateCartCoupon({
      mobile: resolvedMobile,
      code: cart.coupon.couponCode,
      skus: cartSkusForCouponValidation(cart.products),
      cartTotal: Number(cart.totalAmount) || 0,
      storeId: resolvedStoreId || undefined,
    });
    if (!resp?.success) return clearCartCoupon();
    return applyValidatedCouponResponse(resp);
  } catch {
    return clearCartCoupon();
  }
}

/** Re-run validate-coupon for the applied cart coupon (e.g. after store change or checkout entry). */
export async function revalidateAppliedCartCoupon(opts) {
  return revalidateCartCouponOnMutation(opts);
}

function normalizeInstructions(raw) {
  const s = String(raw ?? '').trim();
  return s.slice(0, CART_INSTRUCTIONS_MAX);
}

function emptyCart() {
  return { products: [], totalAmount: 0, totalCount: 0, coupon: null, instructions: '' };
}

async function readCart() {
  const raw = await storageGet(CART_KEY);
  if (raw && Array.isArray(raw.products) && typeof raw.totalAmount === 'number' && typeof raw.totalCount === 'number') {
    return {
      products: raw.products,
      totalAmount: raw.totalAmount,
      totalCount: raw.totalCount,
      coupon: normalizeCoupon(raw.coupon),
      instructions: normalizeInstructions(raw.instructions),
    };
  }
  if (Array.isArray(raw)) {
    return emptyCart();
  }
  return emptyCart();
}

function notifyV2CartChanged() {
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('v2-cart-changed'));
    }
  } catch {
    // ignore
  }
}

async function writeCart(cart) {
  const payload = {
    products: Array.isArray(cart.products) ? cart.products : [],
    totalAmount: Number(cart.totalAmount) || 0,
    totalCount: Number(cart.totalCount) || 0,
    coupon: cart.coupon != null ? normalizeCoupon(cart.coupon) : null,
    instructions: normalizeInstructions(cart.instructions),
  };
  await storageSet(CART_KEY, payload);
  notifyV2CartChanged();
  return payload;
}

function toStoredOption(opt) {
  if (!opt) return null;
  return {
    id: opt.id,
    name: opt.name,
    sku: opt.sku,
    additionalPrice: String(opt.additionalPrice ?? '0'),
    sortOrder: opt.sortOrder ?? 0,
    isDefault: Boolean(opt.isDefault),
  };
}

/**
 * Map ProductDetail selection state → cart customization map (full option objects).
 * Checkbox groups: array of selections in state → stored as one object if single, else array of option objects.
 */
export function resolveCustomizationsForCart(productCustomizations, selected) {
  const out = {};
  const defs = productCustomizations || {};
  for (const [key, sel] of Object.entries(selected || {})) {
    const def = defs[key];
    if (!def?.options?.length) continue;

    if (Array.isArray(sel)) {
      const opts = sel
        .map((id) =>
          def.options.find(
            (o) =>
              String(o.id) === String(id) ||
              String(o.name) === String(id) ||
              String(o.sku).toUpperCase() === String(id).toUpperCase()
          )
        )
        .filter(Boolean)
        .map(toStoredOption);
      if (!opts.length) continue;
      out[key] = opts.length === 1 ? opts[0] : opts;
    } else if (sel != null && sel !== '') {
      const opt = def.options.find(
        (o) =>
          String(o.id) === String(sel) ||
          String(o.name) === String(sel) ||
          String(o.sku).toUpperCase() === String(sel).toUpperCase()
      );
      if (opt) out[key] = toStoredOption(opt);
    }
  }
  return out;
}

export function computeLineUnitPrice(variant, customizationsResolved) {
  let p = Number(variant?.price) || 0;
  for (const v of Object.values(customizationsResolved || {})) {
    const arr = Array.isArray(v) ? v : [v];
    for (const opt of arr) {
      p += Number(opt?.additionalPrice) || 0;
    }
  }
  return p;
}

/** One cart line per product (variant/customizations are not used to split lines). */
export function getCartLineKey({ productId }) {
  return String(productId ?? '');
}

function cartLinesForProduct(products, productId) {
  const pid = String(productId);
  return products
    .map((p, i) => ({ line: p, i }))
    .filter(({ line }) => String(line.productId) === pid);
}

function recomputeTotals(products) {
  let totalAmount = 0;
  let totalCount = 0;
  for (const line of products) {
    const unit = Number(line.price) || 0;
    const q = Number(line.quantity) || 0;
    totalAmount += unit * q;
    totalCount += q;
  }
  return { totalAmount, totalCount };
}

function stripVariantForCart(variant) {
  if (!variant || typeof variant !== 'object') return variant;
  const { isInStock, ...rest } = variant;
  return rest;
}

function productImageUrlFromApi(product) {
  if (!product || typeof product !== 'object') return '';
  if (Array.isArray(product.images) && product.images[0]) return String(product.images[0]).trim();
  if (Array.isArray(product.imgUrls) && product.imgUrls[0]) return String(product.imgUrls[0]).trim();
  if (product.imageUrl) return String(product.imageUrl).trim();
  if (product.imgUrl) return String(product.imgUrl).trim();
  if (product.image) return String(product.image).trim();
  return '';
}

async function enrichFreeSkuDetails(details) {
  if (!details || typeof details !== 'object') return details;
  if (details.imageUrl) return details;
  const pid = details.productId != null ? String(details.productId).trim() : '';
  if (!pid) return details;
  try {
    const product = await getProductById({ id: pid });
    const imageUrl = productImageUrlFromApi(product);
    if (imageUrl) return { ...details, imageUrl };
  } catch {
    // keep line without image; UI falls back to placeholder
  }
  return details;
}

function buildFreeSkuCartLine(details) {
  const variant = details?.variant && typeof details.variant === 'object' ? details.variant : {};
  return {
    productId: details.productId,
    name: details.productName || 'Free item',
    imageUrl: details.imageUrl || '',
    quantity: Math.max(1, Number(details.quantity) || 1),
    price: Number(variant.price) || 0,
    variant: stripVariantForCart(variant),
    customizations: {},
    isFreeSkuItem: true,
  };
}

/** Join customization option names for compact display (e.g. cart row subtitle). */
export function formatCartCustomizationSummary(customizations) {
  if (!customizations || typeof customizations !== 'object') return '';
  const parts = [];
  for (const val of Object.values(customizations)) {
    const arr = Array.isArray(val) ? val : [val];
    for (const o of arr) {
      if (o?.name) parts.push(String(o.name));
    }
  }
  return parts.join(' · ');
}

/**
 * Flat list of chosen add-ons with optional extra price (for order summaries).
 * @returns {{ name: string, additionalPrice: number }[]}
 */
/** Sum of customization add-on prices across all cart lines (× qty). */
export function computeCartCustomizationTotal(products) {
  if (!Array.isArray(products)) return 0;
  let total = 0;
  for (const line of products) {
    const qty = Number(line.quantity) || 0;
    const addOns = listCartCustomizationAddOns(line.customizations);
    const perUnit = addOns.reduce((s, a) => s + (Number(a.additionalPrice) || 0), 0);
    total += perUnit * qty;
  }
  return total;
}

export function listCartCustomizationAddOns(customizations) {
  const out = [];
  if (!customizations || typeof customizations !== 'object') return out;
  for (const val of Object.values(customizations)) {
    const arr = Array.isArray(val) ? val : [val];
    for (const o of arr) {
      if (!o?.name) continue;
      out.push({
        name: String(o.name),
        additionalPrice: Number(o.additionalPrice) || 0,
      });
    }
  }
  return out;
}

export function getAppliedCouponDiscountRupee(cart) {
  const c = cart?.coupon;
  if (!c || !c.couponCode) return 0;
  const v = Math.max(0, Number(c.couponValue) || 0);
  const sub = Math.max(0, Number(cart.totalAmount) || 0);
  return Math.min(sub, v);
}

/** SKUs / ids to send to Pilot coupon validation (variant SKU + product id). */
export function cartSkusForCouponValidation(products) {
  const out = [];
  for (const line of products || []) {
    // Coupon-granted free lines must not count as "free SKU already in cart" on re-validation.
    if (line?.isFreeSkuItem) continue;
    const sku = line?.variant?.sku != null ? String(line.variant.sku).trim() : '';
    if (sku) out.push(sku);
    const pid = line?.productId != null ? String(line.productId).trim() : '';
    if (pid) out.push(pid);
  }
  return [...new Set(out.map((s) => s.toUpperCase()))];
}

/** Full cart document (for Cart page / checkout). */
export async function getCart() {
  return readCart();
}

/**
 * Persist delivery instructions on the v2_cart document (does not clear coupon or lines).
 * @param {string} text
 */
export async function setCartInstructions(text) {
  const cart = await readCart();
  cart.instructions = normalizeInstructions(text);
  return writeCart(cart);
}

/**
 * Apply a successful validate-coupon response (percentage, fixed, or free SKU).
 * @param {object} resp — Pilot validate-coupon JSON body
 */
export async function applyValidatedCouponResponse(resp) {
  if (!resp?.success) {
    throw new Error(resp?.message || 'Invalid coupon');
  }

  const couponRaw = resp.coupon;
  const couponCode = String(resp.couponCode || couponRaw?.couponCode || '').trim().toUpperCase();
  const couponValue = Math.max(0, Number(resp.couponValue ?? couponRaw?.couponValue) || 0);
  if (!couponCode) {
    throw new Error('Invalid coupon');
  }

  const cart = await readCart();

  if (isFreeSkuCouponMeta(couponRaw)) {
    const freeSkuAction = String(resp.freeSkuAction || '').trim();
    const details = resp.freeSkuDetails;

    const coupon = {
      couponCode,
      couponValue,
      couponType: 'free_sku_on_purchase',
      discountType: couponRaw?.discount_type || 'free_sku',
      freeSkuAction,
      freeSkuProductId:
        freeSkuAction === 'auto_add' && details?.productId != null
          ? String(details.productId)
          : null,
      couponTitle: couponRaw?.couponTitle ? String(couponRaw.couponTitle).trim() : undefined,
      freeSkuProductName: details?.productName ? String(details.productName).trim() : undefined,
    };

    cart.products = stripAutoAddedFreeSkuLines(cart.products);

    if (freeSkuAction === 'auto_add' && details?.productId != null) {
      const enriched = await enrichFreeSkuDetails(details);
      cart.products.push(buildFreeSkuCartLine(enriched));
    }

    const { totalAmount, totalCount } = recomputeTotals(cart.products);
    cart.totalAmount = totalAmount;
    cart.totalCount = totalCount;
    cart.coupon = coupon;
    return writeCart(cart);
  }

  const { totalAmount, totalCount } = recomputeTotals(cart.products);
  cart.totalAmount = totalAmount;
  cart.totalCount = totalCount;
  cart.coupon = {
    couponCode,
    couponValue,
    discountType: couponRaw?.discount_type || undefined,
  };
  return writeCart(cart);
}

/**
 * Persist a validated coupon on the current cart (does not clear lines).
 * @deprecated Prefer applyValidatedCouponResponse after validate-coupon.
 * @param {{ couponCode: string, couponValue: number }} coupon
 */
export async function applyCartCoupon(coupon) {
  const cart = await readCart();
  const norm = normalizeCoupon(coupon);
  if (!norm) {
    cart.coupon = null;
    return writeCart(cart);
  }
  cart.coupon = norm;
  return writeCart(cart);
}

/** Remove coupon and any auto-added free SKU line. */
export async function clearCartCoupon() {
  const cart = await readCart();
  if (!cart.coupon) return cart;
  cart.products = stripAutoAddedFreeSkuLines(cart.products);
  const { totalAmount, totalCount } = recomputeTotals(cart.products);
  cart.totalAmount = totalAmount;
  cart.totalCount = totalCount;
  cart.coupon = null;
  return writeCart(cart);
}

/** Clear persisted V2 cart and notify listeners. */
export async function clearCart() {
  return writeCart(emptyCart());
}

/** @deprecated use getCart */
export async function getCartItems() {
  const cart = await readCart();
  return cart.products;
}

export async function getCartLineQuantity({ productId }) {
  const cart = await readCart();
  const matches = cartLinesForProduct(cart.products, productId);
  return matches.reduce((sum, { line }) => sum + (Number(line.quantity) || 0), 0);
}

/** First cart line for this product (one line per product). */
export async function getCartLineForProduct(productId) {
  const cart = await readCart();
  const matches = cartLinesForProduct(cart.products, productId);
  return matches[0]?.line ?? null;
}

export async function setCartLineQuantity({ productId }, qty) {
  const cart = await readCart();
  const matches = cartLinesForProduct(cart.products, productId);
  if (!matches.length) return cart;

  const template = matches[0].line;
  if (template.isFreeSkuItem) {
    const q = Number(qty) || 0;
    if (q <= 0) return clearCartCoupon();
    qty = 1;
  }

  const q = Number(qty) || 0;
  const pid = String(productId);
  cart.products = cart.products.filter((p) => String(p.productId) !== pid);
  if (q > 0) {
    cart.products.push({ ...template, quantity: q });
  }

  const { totalAmount, totalCount } = recomputeTotals(cart.products);
  cart.totalAmount = totalAmount;
  cart.totalCount = totalCount;

  await writeCart(cart);
  if (cart.coupon?.couponCode) {
    return revalidateCartCouponOnMutation();
  }
  return readCart();
}

/**
 * Add or merge a line. Pass resolved customizations (full option objects per key).
 * @param {object} payload
 * @param {string} payload.productId
 * @param {string} payload.name
 * @param {string} payload.imageUrl
 * @param {object} payload.variant
 * @param {Record<string, object|object[]>} payload.customizations
 * @param {number} [payload.qty=1]
 */
export async function addToCartItem(payload) {
  const {
    productId,
    name,
    imageUrl,
    variant,
    customizations,
    qty = 1,
  } = payload;

  const cart = await readCart();
  const customizationsResolved = customizations ?? {};
  const linePrice = computeLineUnitPrice(variant, customizationsResolved);
  const storedVariant = stripVariantForCart(variant);

  const addQty = Number(qty) || 1;
  const pid = String(productId);

  // Paid add replaces a coupon-granted free line for the same product; revalidation picks discount_in_cart.
  cart.products = cart.products.filter(
    (p) => !(String(p.productId) === pid && p.isFreeSkuItem)
  );

  let existingQty = 0;
  cart.products = cart.products.filter((p) => {
    if (String(p.productId) === pid) {
      existingQty += Number(p.quantity) || 0;
      return false;
    }
    return true;
  });

  const isNewLine = existingQty === 0;

  cart.products.push({
    productId,
    urlId: payload.urlId ? String(payload.urlId) : undefined,
    name,
    imageUrl,
    quantity: existingQty + addQty,
    price: linePrice,
    variant: storedVariant,
    customizations: customizationsResolved,
  });

  const { totalAmount, totalCount } = recomputeTotals(cart.products);
  cart.totalAmount = totalAmount;
  cart.totalCount = totalCount;

  if (isNewLine) {
    trackGa4AddToCart({
      productId,
      name,
      price: linePrice,
      quantity: addQty,
    });
  }

  await writeCart(cart);
  if (cart.coupon?.couponCode) {
    return revalidateCartCouponOnMutation();
  }
  return readCart();
}
